import * as React from 'react';
import { css } from '@emotion/react';
import { alpha, FixMeLater, getOuterHeight, inDOM } from '../../utils';
import { ColorValue, TextColorOption, useHoneycombTheme } from '../../theming';
import { useEventCallback, useId } from '../../hooks';
import { Anchor, AnchorProps } from '../Typography';

export type CropperTheme = {
    faderHeight: number;
    buttonBuffer: {
        m: number;
        s: number;
    };
    buttonColor: TextColorOption;
};

const cropperTheme: CropperTheme = {
    faderHeight: 20,
    buttonBuffer: {
        m: 10,
        s: 2,
    },
    buttonColor: 'primary.base',
};

export type CropperProps = {
    /**
     * Cropper cannot crop content that is hidden on mount, as it must be able to measure the content's height.
     * In these cases case, set this prop to `true` until the content has been revealed.
     * @default false
     */
    contentHidden?: boolean;

    /**
     * The content which may need cropping.
     */
    children: React.ReactNode;

    /**
     * The maximum height in pixels which the text can occupy before cropping is applied.
     */
    maxHeight: number;

    /**
     * The alignment of the toggle button.
     * @default center
     */
    buttonAlignment?: 'flex-start' | 'flex-end' | 'center';

    /**
     * Additional properties applied to the toggle button element.
     * @default undefined
     */
    ButtonProps?: AnchorProps;

    /**
     * The distance between the toggle button and the text.
     * @default default
     */
    buttonProximity?: 'close' | 'default';

    /**
     * The caption that will be applied to the toggle button.
     * @default { expanded: 'Less', cropped: 'More' }
     */
    buttonCaption?: { expanded: string; cropped: string };

    /**
     * Callback fired when the toggle button is clicked.
     * @param {React.MouseEvent} event The event source of the callback.
     * @param {boolean} expanded The current expanded state.
     */
    onToggleClick?: (event: React.MouseEvent, expanded: boolean) => void;

    /**
     * If `true`, the default expand action will not be triggered when the toggle button is clicked.
     * This can be used to override the expand action in conjunction with the `onToggleClick` prop.
     */
    suppressExpand?: boolean;

    /**
     * The background color of the content to be cropped. This will be used to apply the fade-out gradient.
     * @default background.content
     */
    contentBackgroundColor?: ColorValue;
};

/**
 * Cropper component allows us to display a long piece of content in a cropper, which can be expanded and collapsed into
 * from the use of more button. The component allows defining the max height we want the content to be displayed and the remainder
 * content gets cropped from the screen. We have full control over the positioning of button which can be placed at the start, end or center under the
 * content. Additionally there are other variables which can be controlled such as contentBackgroundColor to dictate the background of the content, button proximity
 * which controls how close the button is to the content.
 */
export const Cropper = React.forwardRef(function Cropper(
    props: CropperProps,
    ref: React.ForwardedRef<FixMeLater>
): React.JSX.Element {
    const {
        buttonAlignment = 'center',
        buttonProximity = 'default',
        buttonCaption = { expanded: 'Less', cropped: 'More' },
        ButtonProps,
        contentHidden = false,
        children,
        maxHeight,
        onToggleClick,
        suppressExpand: suppressToggle,
        contentBackgroundColor = 'background.content',
        ...other
    } = props;

    const isClient = inDOM();

    const [cropped, setCropped] = React.useState(false);
    const [expanded, setExpanded] = React.useState(false);

    const contentRef = React.useRef(null);

    const contentId = useId();

    const update = React.useCallback((): void => {
        if (contentRef.current == null) {
            return;
        }
        const contentHeight: number = getOuterHeight(contentRef.current);
        const shouldCrop = contentHeight > maxHeight;
        setCropped(shouldCrop);
    }, [maxHeight]);

    const handleToggleClick = useEventCallback((event: React.MouseEvent) => {
        if (!suppressToggle) {
            setExpanded(!expanded);
        }
        if (onToggleClick) {
            onToggleClick(event, !expanded);
        }
    });

    React.useEffect(() => {
        if (isClient && !cropped && !contentHidden) {
            requestAnimationFrame(() => {
                update();
            });
        }
    }, [contentHidden, cropped, isClient, maxHeight, update]);

    const { theme } = useHoneycombTheme();

    const buttonBuffer = cropperTheme.buttonBuffer[buttonProximity === 'close' ? 's' : 'm'];

    const faderColor = theme.fn.resolveColorValue(contentBackgroundColor);

    const expanderStyles = css`
        position: relative;
        ${!cropped &&
        `
            max-height: none;
            overflow: visible;
        `}
        ${cropped &&
        `
            overflow: hidden;
        `}
        ${!expanded &&
        cropped &&
        `
            max-height: ${maxHeight}px;
        `}
    `;

    const faderStyles = css`
        height: ${cropperTheme.faderHeight}px;
        position: absolute;
        bottom: 0;
        width: 100%;
        background: linear-gradient(to bottom, transparent, ${alpha(faderColor, 0.8)} 50%, ${faderColor} 90%);
    `;

    const footerStyles = css`
        display: flex;
        padding-top: ${buttonBuffer}px;
        justify-content: ${buttonAlignment};
    `;

    const fader = cropped ? <div css={faderStyles} hidden={expanded} /> : null;

    const button = cropped ? (
        <Anchor
            component="button"
            underline="always"
            truncate={true}
            size="s"
            color="text.link"
            onClick={handleToggleClick}
            aria-expanded={expanded}
            aria-controls={contentId}
            data-cropper-button={true}
            {...ButtonProps}
        >
            {expanded ? buttonCaption.expanded : buttonCaption.cropped}
        </Anchor>
    ) : null;

    return (
        <div ref={ref} {...other} data-cropper={true}>
            <div css={expanderStyles} aria-expanded={expanded} id={contentId}>
                <div ref={contentRef}>{children}</div>
                {fader}
            </div>
            <div css={footerStyles}>{button}</div>
        </div>
    );
});
